import MoreVert from "@mui/icons-material/MoreVert";
import { Box, CircularProgress, IconButton } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { type FC, type ReactNode, useId, useState } from "react";
import { SURFACE } from "../../../theme/tokens";
import { blockTypographySx } from "../utils";
import type { RowElement } from "./DailyRow";

export type InnerBlockProps = {
	innerRow: RowElement;
	hideMainTextOnHover: boolean;
};

const MainShortText = ({ text, hideOnHover, hovered }: { text: ReactNode; hideOnHover: boolean; hovered: boolean }) => {
	return (
		<Box
			sx={{
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
				transition: "opacity 0.3s",
				opacity: hideOnHover && hovered ? 0 : 1,
				color: "inherit",
			}}
		>
			{hideOnHover && hovered ? null : text}
		</Box>
	);
};

export const InnerBlock: FC<InnerBlockProps> = ({ innerRow, hideMainTextOnHover }) => {
	const descriptionId = useId();
	const [isMouseHovered, setIsMouseHovered] = useState(false);
	const [isFocusWithin, setIsFocusWithin] = useState(false);
	const isHovered = isMouseHovered || isFocusWithin;
	const { isOptimisticLoading, isBeingMoved } = innerRow;

	return (
		<Box
			id={innerRow.domId}
			role={innerRow.accessibleLabel ? "note" : undefined}
			aria-label={innerRow.accessibleLabel}
			aria-describedby={innerRow.accessibleDescription ? descriptionId : undefined}
			tabIndex={innerRow.accessibleLabel ? 0 : undefined}
			onFocus={() => setIsFocusWithin(true)}
			onBlur={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget)) setIsFocusWithin(false);
			}}
			onMouseEnter={() => {
				setIsMouseHovered(true);
			}}
			onMouseLeave={() => {
				setIsMouseHovered(false);
			}}
			sx={(theme) => ({
				display: "flex",
				width: "100%",
				justifyContent: "space-between",
				alignItems: "center",
				bgcolor: innerRow.bgColor,
				backgroundImage: innerRow.backgroundImage,
				gap: 1,
				...blockTypographySx,
				color: innerRow.color || blockTypographySx.color,
				...(innerRow.fontWeight !== undefined && { fontWeight: innerRow.fontWeight }),
				...(isBeingMoved && {
					opacity: 0.4,
					filter: "grayscale(50%)",
				}),

				...(isOptimisticLoading && {
					border: `2px dashed ${theme.palette.primary.main}`,
					animation: "pulse 1.5s ease-in-out infinite",
					"@keyframes pulse": {
						"0%, 100%": { opacity: 0.7 },
						"50%": { opacity: 1 },
					},
				}),
			})}
		>
			{innerRow.accessibleDescription && (
				<Box component="span" id={descriptionId} sx={visuallyHidden}>
					{innerRow.accessibleDescription}
				</Box>
			)}
			<Box
				sx={{
					color: "inherit",
					display: "flex",
					alignItems: "center",
					justifyContent: "start",
					gap: 1,
					flex: 1,
					minWidth: 0,
					overflow: "hidden",
				}}
			>
				{isOptimisticLoading && <CircularProgress size={12} sx={(theme) => ({ color: theme.palette.primary.main })} />}
				<MainShortText text={innerRow.mainShortText} hideOnHover={hideMainTextOnHover} hovered={isHovered} />
				{!isOptimisticLoading && innerRow.shortTextTrailingNode?.(isHovered, hideMainTextOnHover)}
			</Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					blockTypographySx,
				}}
			>
				{[innerRow.leadingBadge, innerRow.badge].map((badge, idx) =>
					badge ? (
						<Box
							key={idx === 0 ? "leading" : "trailing"}
							component={"button"}
							onClick={badge.onBadgeClick}
							sx={(theme) => ({
								border: "none",
								cursor: badge.onBadgeClick ? "pointer" : "default",
								":hover": badge.onBadgeClick
									? {
											opacity: 0.9,
										}
									: undefined,
								bgcolor: theme.palette.primary.main,
								color: SURFACE,
								fontWeight: 500,
								fontSize: "11px",
								px: 1,
								py: "2px",
								borderRadius: 0.5,
								height: "fit-content",
								...badge.badgeSx,
							})}
						>
							{badge.text}
						</Box>
					) : null,
				)}
				{innerRow.onRowClick && (
					<IconButton
						size={"small"}
						onClick={innerRow.onRowClick}
						sx={{
							p: 0,
							m: 0,
							opacity: isHovered ? 1 : 0,
						}}
					>
						<MoreVert fontSize={"inherit"} />
					</IconButton>
				)}
			</Box>
		</Box>
	);
};
